import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { PayService } from 'src/app/service/pay.service';

type BusinessTabKey = 'caissier' | 'achats' | 'depots' | 'retraits' | 'codes' | 'mensualites' | 'tarif';
type FilterTabKey = 'achats' | 'depots' | 'retraits' | 'mensualites';

@Component({
  selector: 'app-bussness-information',
  templateUrl: './bussness-information.component.html',
  styleUrls: ['./bussness-information.component.css'],
  standalone: false
})
export class BussnessInformationComponent implements OnInit {
  userid: any;

  baccountList: any[] = [];
  selectedBusinessId: number | null = null;
  selectedBusiness: any | null = null;

  caissierlist: any[] = [];
  achatlist: any[] = [];
  depotlist: any[] = [];
  retraitlist: any[] = [];
  codeList: any[] = [];
  mensualiteList: any[] = [];

  loadingAccounts = false;
  loadingBusinessLists = false;
  listError = '';

  activeTab: BusinessTabKey = 'caissier';

  readonly monthOptions = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Fevrier' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Aout' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Decembre' }
  ];

  achatsYearOptions: number[] = [];
  depotsYearOptions: number[] = [];
  retraitsYearOptions: number[] = [];
  mensualitesYearOptions: number[] = [];

  selectedAchatsMonth: number | null = null;
  selectedAchatsYear: number | null = null;
  selectedDepotsMonth: number | null = null;
  selectedDepotsYear: number | null = null;
  selectedRetraitsMonth: number | null = null;
  selectedRetraitsYear: number | null = null;
  selectedMensualitesMonth: number | null = null;
  selectedMensualitesYear: number | null = null;

  constructor(private userService: PayService, private routerActivate: ActivatedRoute) {
  }

  ngOnInit(): void {
    this.routerActivate.params.subscribe(params => {
      this.userid = params['id'];
      this.getInformationsSurcompte();
    });
  }

  setActiveTab(tab: BusinessTabKey): void {
    this.activeTab = tab;
  }

  getInformationsSurcompte(): void {
    const previousSelectedId = this.selectedBusinessId;
    this.loadingAccounts = true;

    this.userService
      .getBussnessAcountsUtilisateur(this.userid)
      .pipe(finalize(() => (this.loadingAccounts = false)))
      .subscribe({
        next: (data: any[]) => {
          this.baccountList = Array.isArray(data) ? data : [];

          if (this.baccountList.length === 0) {
            this.selectedBusinessId = null;
            this.selectedBusiness = null;
            this.resetBusinessLists();
            this.resetYearOptions();
            return;
          }

          const preferredId = this.resolvePreferredBusinessId(previousSelectedId);
          this.selectBusinessAccount(preferredId);
        },
        error: () => {
          this.baccountList = [];
          this.selectedBusinessId = null;
          this.selectedBusiness = null;
          this.resetBusinessLists();
          this.resetYearOptions();
        }
      });
  }

  selectBusinessAccount(accountId: number): void {
    this.selectedBusinessId = Number(accountId);
    this.selectedBusiness =
      this.baccountList.find((account: any) => Number(account.id) === this.selectedBusinessId) || null;

    if (this.selectedBusinessId == null || Number.isNaN(this.selectedBusinessId)) {
      this.resetBusinessLists();
      this.resetYearOptions();
      return;
    }

    this.loadBusinessCollections(this.selectedBusinessId);
  }

  activerBaccount(accountid: number): void {
    this.userService.activerBaccounUser(accountid).subscribe(() => {
      this.getInformationsSurcompte();
    });
  }

  desactiverBaccount(accountid: number): void {
    this.userService.desactiverAccountUser(accountid).subscribe(() => {
      this.getInformationsSurcompte();
    });
  }

  getAccountNumberDisplay(accountNumber: string | number | null | undefined): string {
    const raw = `${accountNumber ?? ''}`.replace(/\s+/g, '');
    if (raw.length === 0) {
      return '-';
    }
    return raw.match(/.{1,4}/g)?.join(' ') || raw;
  }

  getEmptyListMessage(tab: 'caissier' | 'achats' | 'depots' | 'retraits' | 'codes' | 'mensualites'): string {
    const businessName = this.selectedBusiness?.nomEntreprise || 'ce compte business';

    if (tab === 'achats' || tab === 'depots' || tab === 'retraits' || tab === 'mensualites') {
      if (this.hasActiveFilter(tab)) {
        return `Aucun resultat pour le filtre mois/annee selectionne (${businessName}).`;
      }
    }

    switch (tab) {
      case 'caissier':
        return `Aucun caissier enregistre pour ${businessName}.`;
      case 'achats':
        return `Aucun achat enregistre pour ${businessName}.`;
      case 'depots':
        return `Aucun depot enregistre pour ${businessName}.`;
      case 'retraits':
        return `Aucun retrait enregistre pour ${businessName}.`;
      case 'codes':
        return `Aucun code de retrait genere pour ${businessName}.`;
      case 'mensualites':
        return `Aucune mensualite disponible pour ${businessName}.`;
      default:
        return 'Aucune donnee disponible.';
    }
  }

  get filteredAchatsList(): any[] {
    return this.applyDateFilter(this.achatlist, this.selectedAchatsMonth, this.selectedAchatsYear, 'dateAchat');
  }

  get filteredDepotsList(): any[] {
    return this.applyDateFilter(this.depotlist, this.selectedDepotsMonth, this.selectedDepotsYear, 'date');
  }

  get filteredRetraitsList(): any[] {
    return this.applyDateFilter(this.retraitlist, this.selectedRetraitsMonth, this.selectedRetraitsYear, 'date');
  }

  get filteredMensualitesList(): any[] {
    return this.applyMensualiteFilter(this.mensualiteList, this.selectedMensualitesMonth, this.selectedMensualitesYear);
  }

  resetPeriodFilter(tab: FilterTabKey): void {
    if (tab === 'achats') {
      this.selectedAchatsMonth = null;
      this.selectedAchatsYear = null;
      return;
    }

    if (tab === 'depots') {
      this.selectedDepotsMonth = null;
      this.selectedDepotsYear = null;
      return;
    }

    if (tab === 'retraits') {
      this.selectedRetraitsMonth = null;
      this.selectedRetraitsYear = null;
      return;
    }

    this.selectedMensualitesMonth = null;
    this.selectedMensualitesYear = null;
  }

  hasActiveFilter(tab: FilterTabKey): boolean {
    if (tab === 'achats') {
      return this.selectedAchatsMonth != null || this.selectedAchatsYear != null;
    }

    if (tab === 'depots') {
      return this.selectedDepotsMonth != null || this.selectedDepotsYear != null;
    }

    if (tab === 'retraits') {
      return this.selectedRetraitsMonth != null || this.selectedRetraitsYear != null;
    }

    return this.selectedMensualitesMonth != null || this.selectedMensualitesYear != null;
  }

  private resolvePreferredBusinessId(previousSelectedId: number | null): number {
    if (previousSelectedId != null) {
      const exists = this.baccountList.some((account: any) => Number(account.id) === Number(previousSelectedId));
      if (exists) {
        return Number(previousSelectedId);
      }
    }

    return Number(this.baccountList[0].id);
  }

  private loadBusinessCollections(accountId: number): void {
    this.loadingBusinessLists = true;
    this.listError = '';

    forkJoin({
      caissiers: this.safeList(this.userService.getBussnessAccountCaissiers(accountId)),
      achats: this.safeList(this.userService.getBussnessAccountAchats(accountId)),
      depots: this.safeList(this.userService.getBussnessAccountDepots(accountId)),
      retraits: this.safeList(this.userService.getBussnessAccountRetraits(accountId)),
      codes: this.safeList(this.userService.getBussnessAccountCodeRetraits(accountId)),
      mensualites: this.safeList(this.userService.getBussnessAccountMensualites(accountId))
    })
      .pipe(finalize(() => (this.loadingBusinessLists = false)))
      .subscribe({
        next: (data: any) => {
          this.caissierlist = data.caissiers;
          this.achatlist = data.achats;
          this.depotlist = data.depots;
          this.retraitlist = data.retraits;
          this.codeList = data.codes;
          this.mensualiteList = data.mensualites;
          this.resetPeriodFilters();
          this.updateYearOptions();
        },
        error: () => {
          this.resetBusinessLists();
          this.resetYearOptions();
          this.resetPeriodFilters();
          this.listError = 'Impossible de charger les donnees du compte business selectionne.';
        }
      });
  }

  private updateYearOptions(): void {
    this.achatsYearOptions = this.buildYearOptionsFromDate(this.achatlist, 'dateAchat');
    this.depotsYearOptions = this.buildYearOptionsFromDate(this.depotlist, 'date');
    this.retraitsYearOptions = this.buildYearOptionsFromDate(this.retraitlist, 'date');
    this.mensualitesYearOptions = this.buildYearOptionsFromField(this.mensualiteList, 'annee');
  }

  private buildYearOptionsFromDate(list: any[], dateField: string): number[] {
    const years = new Set<number>();
    (Array.isArray(list) ? list : []).forEach(item => {
      const value = item?.[dateField];
      if (!value) {
        return;
      }

      const date = new Date(value);
      const year = date.getFullYear();
      if (!Number.isNaN(year)) {
        years.add(year);
      }
    });

    return Array.from(years).sort((a, b) => b - a);
  }

  private buildYearOptionsFromField(list: any[], yearField: string): number[] {
    const years = new Set<number>();
    (Array.isArray(list) ? list : []).forEach(item => {
      const year = Number(item?.[yearField]);
      if (!Number.isNaN(year) && year > 0) {
        years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }

  private applyDateFilter(
    list: any[],
    selectedMonth: number | null,
    selectedYear: number | null,
    dateField: string
  ): any[] {
    if (!Array.isArray(list) || list.length === 0) {
      return [];
    }

    if (selectedMonth == null && selectedYear == null) {
      return list;
    }

    return list.filter(item => {
      const rawDate = item?.[dateField];
      if (!rawDate) {
        return false;
      }

      const date = new Date(rawDate);
      if (Number.isNaN(date.getTime())) {
        return false;
      }

      const monthMatch = selectedMonth == null || date.getMonth() + 1 === selectedMonth;
      const yearMatch = selectedYear == null || date.getFullYear() === selectedYear;
      return monthMatch && yearMatch;
    });
  }

  private applyMensualiteFilter(
    list: any[],
    selectedMonth: number | null,
    selectedYear: number | null
  ): any[] {
    if (!Array.isArray(list) || list.length === 0) {
      return [];
    }

    if (selectedMonth == null && selectedYear == null) {
      return list;
    }

    return list.filter(item => {
      const month = Number(item?.month);
      const year = Number(item?.annee);

      const monthMatch = selectedMonth == null || month === selectedMonth;
      const yearMatch = selectedYear == null || year === selectedYear;

      return monthMatch && yearMatch;
    });
  }

  private safeList(source$: Observable<any>): Observable<any[]> {
    return source$.pipe(catchError(() => of([])));
  }

  private resetBusinessLists(): void {
    this.caissierlist = [];
    this.achatlist = [];
    this.depotlist = [];
    this.retraitlist = [];
    this.codeList = [];
    this.mensualiteList = [];
  }

  private resetPeriodFilters(): void {
    this.selectedAchatsMonth = null;
    this.selectedAchatsYear = null;
    this.selectedDepotsMonth = null;
    this.selectedDepotsYear = null;
    this.selectedRetraitsMonth = null;
    this.selectedRetraitsYear = null;
    this.selectedMensualitesMonth = null;
    this.selectedMensualitesYear = null;
  }

  private resetYearOptions(): void {
    this.achatsYearOptions = [];
    this.depotsYearOptions = [];
    this.retraitsYearOptions = [];
    this.mensualitesYearOptions = [];
  }
}
